import { Link } from 'react-router-dom';
import { Linkedin, Monitor, Sun, MoonStar } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTheme } from '@/context/ThemeContext';

const THEME_OPTIONS = [
    { icon: Monitor, value: 'system', label: 'System' },
    { icon: Sun, value: 'light', label: 'Light' },
    { icon: MoonStar, value: 'dark', label: 'Dark' },
] as const;

const Footer = () => {
    const { theme, setTheme } = useTheme();

    return (
        <footer className="relative bg-overlay/[0.02] pt-16 pb-8 overflow-hidden border-t border-overlay/[0.06]">
            <div className="container relative z-10 px-4 md:px-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-10 md:gap-8 mb-16">
                    {/* Brand */}
                    <div className="md:col-span-1">
                        <h2 className="text-xl font-bold text-foreground mb-3">Antz.ai</h2>
                        <p className="text-sm text-muted-foreground leading-relaxed max-w-xs">
                            Pioneering the future of autonomous enterprise with advanced AI agents.
                        </p>
                    </div>

                    {/* Solutions */}
                    <div>
                        <h3 className="text-sm font-semibold text-foreground mb-4">Solutions</h3>
                        <ul className="space-y-3">
                            <li><Link to="/products" className="text-sm text-muted-foreground hover:text-foreground transition-colors">AI Agents</Link></li>
                            <li><a href="/#services" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Process Automation</a></li>
                            <li><a href="/#value-prop" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Data Intelligence</a></li>
                            <li><a href="/#services" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Enterprise Consulting</a></li>
                        </ul>
                    </div>

                    {/* Company */}
                    <div>
                        <h3 className="text-sm font-semibold text-foreground mb-4">Company</h3>
                        <ul className="space-y-3">
                            <li><Link to="/about-us" className="text-sm text-muted-foreground hover:text-foreground transition-colors">About Us</Link></li>
                            <li><Link to="/careers" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Careers</Link></li>
                            <li><Link to="/contact-us" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Contact</Link></li>
                            <li><Link to="/products#faq" className="text-sm text-muted-foreground hover:text-foreground transition-colors">FAQ</Link></li>
                        </ul>
                    </div>

                    {/* Legal */}
                    <div>
                        <h3 className="text-sm font-semibold text-foreground mb-4">Legal</h3>
                        <ul className="space-y-3">
                            <li><Link to="/privacy-policy" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Privacy Policy</Link></li>
                            <li><Link to="/terms-of-service" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Terms of Service</Link></li>
                        </ul>
                    </div>

                    {/* Connect */}
                    <div>
                        <h3 className="text-sm font-semibold text-foreground mb-4">Connect</h3>
                        <ul className="space-y-3">
                            <li>
                                <a
                                    href="https://www.linkedin.com/company/antz-ai"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
                                >
                                    <Linkedin className="w-4 h-4" />
                                    LinkedIn
                                </a>
                            </li>
                        </ul>
                    </div>
                </div>

                {/* Copyright + Theme Toggle */}
                <div className="pt-6 flex flex-col sm:flex-row items-center justify-between gap-6 sm:gap-0">
                    <p className="text-xs text-muted-foreground order-2 sm:order-1">
                        © {new Date().getFullYear()} Antz.ai. All rights reserved.
                    </p>

                    <div
                        className="inline-flex items-center rounded-md border border-overlay/[0.1] bg-overlay/[0.04] overflow-hidden order-1 sm:order-2"
                        role="radiogroup"
                        aria-label="Theme"
                    >
                        {THEME_OPTIONS.map((option) => (
                            <button
                                key={option.value}
                                role="radio"
                                aria-checked={theme === option.value}
                                aria-label={`Switch to ${option.label} theme`}
                                onClick={() => setTheme(option.value)}
                                className={cn(
                                    'relative flex size-7 items-center justify-center rounded-md transition-colors duration-200',
                                    theme === option.value
                                        ? 'text-foreground'
                                        : 'text-muted-foreground hover:text-foreground',
                                )}
                            >
                                {theme === option.value && (
                                    <span className="absolute inset-0 rounded-md border border-muted-foreground/50" />
                                )}
                                <option.icon className="size-3.5" />
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* Large watermark */}
            <div
                aria-hidden="true"
                className="absolute bottom-0 left-0 right-0 flex items-end justify-center pointer-events-none select-none overflow-hidden"
            >
                <span className="text-[6rem] sm:text-[12rem] md:text-[20rem] font-black tracking-widest text-overlay/[0.08] leading-none translate-y-[15%] opacity-100">
                    ANTZ
                </span>
            </div>
        </footer>
    );
};

export default Footer;
